import { useEffect } from 'react';
import styled from 'styled-components';
import { Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import Humanize from 'humanize-plus';
import {
  Breadcrumbs,
  Flex,
  FlexColumn,
  FloatingActionsMenu,
} from '../../../components';
import Table, {
  BaseTableProps,
  DisplayPanel,
  GroupedTable,
  TableEmptyState,
  TableFilters,
  ToggleColumnsMenu,
} from '../../../components/Table';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import useNavigateWithEnvParam from '../../../hooks/useNavigateWithEnvParam';
import useSearchParams from '../../../hooks/useSearchParams';
import useViewLoadTracking from '../../../hooks/useViewLoadTracking';
import { MODEL_RUNS_TABLE_FILTERS } from '../../../constants/urlParams';
import { ModelRuns as ModelRunsType } from '../../../types';
import { isInSaaSMode } from '../../../utils/common';
import { ModelRunsProps } from '../types';
import { fontSize, radius, space } from '../../../style/utils';

const DaysBack = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.secondary};
  background: ${({ theme }) => theme.palette.surface.subtle};
  padding: ${space('sm', 'lg')};
  border-radius: ${radius('sm')};
`;

const TableWrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.base};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: ${space('xxl')};
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  padding-bottom: ${space('lg')};
  width: 100%;
  justify-content: space-between;
`;

const ModelRuns = ({
  tableData,
  columns,
  isLoading,
  search,
  onSearch,
  daysBack,
  isControlled,
  isFilterOptionsLoading,
  searchFunc,
  onScrollToBottom,
  bottomScrollPadding,
  showFloatingActionsMenu,
  expandedRowRenderer,
  pageTitle = 'Model Performance',
  groupBy,
  onGroupByChange,
  groupableColumnIds,
  groupedData,
  expandedGroups,
  onGroupToggle,
  onGroupScrollToBottom,
}: ModelRunsProps) => {
  const isSaaS = isInSaaSMode();
  const isGrouped = !!groupedData;
  const breadcrumbs = useBreadcrumbs({
    pageTitle,
  });

  const { id: selectedRunId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigateWithEnvParam();

  useViewLoadTracking({
    name: pageTitle,
    loadDeps: [tableData],
  });

  useEffect(() => {
    if (selectedRunId && !isLoading && !isGrouped) {
      const isSelectedRunExists = tableData.some(
        (modelRun) => modelRun.unique_id === selectedRunId,
      );

      if (!isSelectedRunExists) {
        navigate(`/report/model-runs?${searchParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunId, tableData, isLoading, isGrouped]);

  const commonTableProps: BaseTableProps<ModelRunsType> = {
    columns,
    search,
    expandedRowRenderer,
    defaultSortKey: 'last_exec_time',
    defaultSortDirection: 'desc',
    idKey: 'unique_id',
    urlParamsPrefix: MODEL_RUNS_TABLE_FILTERS,
    localStorageKey: 'model_runs',
    emptyStateRenderer: TableEmptyState,
    isLoading,
    isFilterOptionsLoading,
  };

  return (
    <Stack sx={{ width: '100%', height: '100%', p: 3, position: 'relative' }}>
      <Stack sx={{ mb: 3, gap: 3, width: '100%' }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <DaysBack>Last {daysBack} Days</DaysBack>
        </Stack>
      </Stack>

      <TableWrapper>
        <TableHeader>
          <Flex gap={space('xxl')}>
            <TableFilters
              data={tableData}
              columns={columns}
              urlParamsPrefix={MODEL_RUNS_TABLE_FILTERS}
              search={search}
              onSearch={onSearch}
              clearFiltersChipLabel={`All (${Humanize.intComma(
                tableData.length,
              )})`}
              disabled={isLoading}
            />
          </Flex>
          {isSaaS ? (
            <DisplayPanel
              columns={columns}
              localStorageKey="model_runs"
              groupBy={groupBy}
              onGroupByChange={onGroupByChange}
              groupableColumnIds={groupableColumnIds}
            />
          ) : (
            <ToggleColumnsMenu columns={columns} localStorageKey="model_runs" />
          )}
        </TableHeader>
        {isGrouped ? (
          <GroupedTable
            {...commonTableProps}
            data={groupedData}
            expandedGroups={expandedGroups}
            onGroupToggle={onGroupToggle}
            onGroupScrollToBottom={onGroupScrollToBottom}
            showEmptyGroups={false}
            isControlled
            bottomScrollPadding={100}
          />
        ) : (
          <Table
            {...commonTableProps}
            data={tableData}
            searchFunc={searchFunc}
            scrollItemId={selectedRunId}
            onScrollToBottom={onScrollToBottom}
            isControlled={isControlled}
            bottomScrollPadding={bottomScrollPadding}
          />
        )}
      </TableWrapper>
      {showFloatingActionsMenu && <FloatingActionsMenu />}
    </Stack>
  );
};

export default ModelRuns;
